import { useQuery } from '@tanstack/react-query';
import { useCallback, useContext, useMemo } from 'react';

import { Typography } from '@/shared/ui';

import { ServicesContext } from '@/contexts/Services/ServicesContext';

import api from '@/api';
import { queryKeys } from '@/api/queryKeys';
import { UserService } from '@/api/types';

import { InternalServicesCategories, InternalServicesList, SearchServiceForm } from './children';
import { useServicesFilters } from './hooks/useServicesFilters';

import './InternalServicesPage.scss';

const InternalServicesPage: React.FC = () => {
  const { services } = useContext(ServicesContext);
  const { isLoading: isCategoriesLoading, data: categories = [] } = useQuery({
    queryKey: queryKeys.services.categories(),
    queryFn: () => api.services.categories().then((r) => r.data),
  });

  const defaultCategories = useMemo<string[]>(() => {
    const uniqueCategoryIds = Array.from(new Set(services?.flatMap((service) => service.categories) || []));

    return categories.filter((category) => uniqueCategoryIds.includes(category.id)).map((category) => category.name);
  }, [categories, services]);

  const { query, selectedCategories, onFilter } = useServicesFilters(defaultCategories);

  const onSubmit = useCallback(
    (searchTerm: string): void => {
      onFilter('query', searchTerm);
    },
    [onFilter],
  );

  const filteredServices = useMemo<UserService[] | null>(() => {
    if (!services) return null;

    return services
      .filter((service) => !service.is_hidden)
      .filter((service) => {
        const formattedQuery = query.trim().toLowerCase();
        const { name, code, source } = service;

        return [name, code, source].some(
          (value) => typeof value === 'string' && value.toLowerCase().includes(formattedQuery),
        );
      })
      .filter((service) => {
        if (selectedCategories.length === defaultCategories.length) {
          return true;
        }

        const selectedCategoryIds = categories?.reduce<number[]>((acc, category) => {
          if (selectedCategories.includes(category.name)) {
            acc.push(category.id);
          }
          return acc;
        }, []);

        return service.categories.some((categoryId) => selectedCategoryIds?.includes(categoryId));
      });
  }, [services, query, categories, selectedCategories, defaultCategories]);

  return (
    <section id="services" className="internal-services-page">
      <div className="container">
        <Typography variant="H1" className="internal-services-page__header">
          Послуги
        </Typography>
        <div className="internal-services-page__content">
          <div className="internal-services-page__search-block">
            <SearchServiceForm defaultValue={query} onSubmit={onSubmit} />
            <InternalServicesCategories
              isCategoriesLoading={isCategoriesLoading}
              onFilter={onFilter}
              selectedCategories={selectedCategories}
              defaultCategories={defaultCategories}
            />
          </div>
          <div className="internal-services-page__services-block">
            <InternalServicesList list={filteredServices} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default InternalServicesPage;
